import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { logout } from '../utils/auth';
import './RaceDetail.css';
import PageHeader from './PageHeader';

const fetchOptions = { credentials: 'include', headers: { 'Content-Type': 'application/json' } };

const RaceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [race, setRace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRace = useCallback(async () => {
    try {
      const response = await fetch(`/api/races/${id}`, fetchOptions);
      if (response.status === 401) { logout(); navigate('/login'); return; }
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to load race');
        return;
      }
      const data = await response.json();
      setRace(data);
    } catch (err) {
      setError('Failed to load race details');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchRace();
  }, [fetchRace]);

  if (loading) return <div className="race-detail"><p>Loading race details...</p></div>;
  if (error) return <div className="race-detail"><p>Error: {error}</p></div>;
  if (!race) return null;

  const raceDate = race.startTime ? new Date(race.startTime) : null;
  const status = race.status || 'upcoming';

  return (
    <div className="race-detail">
      <div className="race-detail-header">
        <PageHeader overline="Formula 1 Prediction Game" title="RACE DETAIL" />
        <button className="nav-button" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>

      <div className="race-detail-card">
        <h2>{race.name}</h2>
        <span className={`status-badge status-${status}`}>{status}</span>
        <div className="race-info-grid">
          {race.year && (
            <div className="race-info-item">
              <div className="race-info-label">Year</div>
              <div className="race-info-value">{race.year}</div>
            </div>
          )}
          {race.round && (
            <div className="race-info-item">
              <div className="race-info-label">Round</div>
              <div className="race-info-value">#{race.round}</div>
            </div>
          )}
          {race.circuit && (
            <div className="race-info-item">
              <div className="race-info-label">Circuit</div>
              <div className="race-info-value">📍 {race.circuit}</div>
            </div>
          )}
          {race.location && (
            <div className="race-info-item">
              <div className="race-info-label">Location</div>
              <div className="race-info-value">{race.location}</div>
            </div>
          )}
          {raceDate && (
            <div className="race-info-item">
              <div className="race-info-label">Date</div>
              <div className="race-info-value">
                {raceDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>
          )}
          {raceDate && (
            <div className="race-info-item">
              <div className="race-info-label">Time</div>
              <div className="race-info-value">{raceDate.toLocaleTimeString()}</div>
            </div>
          )}
        </div>
      </div>

      {status === 'completed' && race.results && (
        <div className="race-detail-section">
          <h3>Race Results</h3>
          <div className="results-podium">
            {race.results.first && (
              <div className="podium-position">
                <span className="podium-medal">🥇</span>
                <span className="podium-driver-name">
                  {race.results.first.name
                    ? <Link to={`/drivers/${race.results.first._id}`} className="driver-link">{race.results.first.name}</Link>
                    : '—'}
                </span>
              </div>
            )}
            {race.results.second && (
              <div className="podium-position">
                <span className="podium-medal">🥈</span>
                <span className="podium-driver-name">
                  {race.results.second.name
                    ? <Link to={`/drivers/${race.results.second._id}`} className="driver-link">{race.results.second.name}</Link>
                    : '—'}
                </span>
              </div>
            )}
            {race.results.third && (
              <div className="podium-position">
                <span className="podium-medal">🥉</span>
                <span className="podium-driver-name">
                  {race.results.third.name
                    ? <Link to={`/drivers/${race.results.third._id}`} className="driver-link">{race.results.third.name}</Link>
                    : '—'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {race.drivers && race.drivers.length > 0 && (
        <div className="race-detail-section">
          <h3>Competing Drivers</h3>
          <ul className="drivers-list">
            {race.drivers.map(driver => (
              <li key={driver._id} className="driver-list-item">
                <span className="driver-number">#{driver.number}</span>
                <Link to={`/drivers/${driver._id}`} className="driver-link">{driver.name}</Link>
                <span className="driver-team">{driver.team}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RaceDetail;
